import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Phone, AlertTriangle, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { PhoneInput } from '../ui/PhoneInput';
import { apiClient } from '../../services/api';

export type AdminContactTarget =
  | { kind: 'player'; userId: string }
  | { kind: 'super_agent'; superAgentId: string }
  | { kind: 'agent'; agentId: string }
  | { kind: 'shop'; agentId: string; shopId: string };

export type AdminUpdateContactModalProps = {
  open: boolean;
  onClose: () => void;
  target: AdminContactTarget;
  currentEmail?: string | null;
  currentPhone?: string | null;
  /** Current verified flags — used to show risk checkbox before first save */
  emailVerified?: boolean | null;
  phoneVerified?: boolean | null;
  title?: string;
  onSuccess?: () => void;
};

type ContactBody = {
  email?: string;
  phone?: string;
  reason: string;
  confirmRisk?: boolean;
};

function extractError(err: unknown): {
  message: string;
  code?: string;
  preflight?: {
    willBlockWithdrawals?: boolean;
    pendingWithdrawalCount?: number;
    remainingVerifiedChannels?: string[];
  };
} {
  const anyErr = err as {
    message?: string;
    response?: { data?: { message?: string; code?: string; preflight?: any } };
  };
  const data = anyErr?.response?.data;
  return {
    message: data?.message || anyErr?.message || 'Failed to update contact',
    code: data?.code,
    preflight: data?.preflight,
  };
}

async function patchContact(target: AdminContactTarget, body: ContactBody) {
  switch (target.kind) {
    case 'player':
      return apiClient.patchAdminUserContact(target.userId, body);
    case 'super_agent':
      return apiClient.patchAdminSuperAgentContact(target.superAgentId, body);
    case 'agent':
      return apiClient.patchAdminAgentContact(target.agentId, body);
    case 'shop':
      return apiClient.patchAdminShopContact(target.agentId, target.shopId, body);
    default:
      throw new Error('Unknown contact target');
  }
}

function phonesLookDifferent(a: string, b: string): boolean {
  const na = a.trim().replace(/\s/g, '');
  const nb = b.trim().replace(/\s/g, '');
  if (!na && !nb) return false;
  if (!na || !nb) return na !== nb;
  return na !== nb;
}

const AdminUpdateContactModal: React.FC<AdminUpdateContactModalProps> = ({
  open,
  onClose,
  target,
  currentEmail,
  currentPhone,
  emailVerified = false,
  phoneVerified = false,
  title = 'Update email / phone',
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [serverRiskHint, setServerRiskHint] = useState<string | null>(null);
  const [confirmRisk, setConfirmRisk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail(currentEmail || '');
    setPhone(currentPhone || '');
    setReason('');
    setError(null);
    setServerRiskHint(null);
    setConfirmRisk(false);
  }, [open, currentEmail, currentPhone]);

  const nextEmail = email.trim().toLowerCase();
  const nextPhone = phone.trim();
  const prevEmail = (currentEmail || '').trim().toLowerCase();
  const prevPhone = (currentPhone || '').trim();
  const emailChanged = Boolean(nextEmail && nextEmail !== prevEmail);
  const phoneChanged = Boolean(nextPhone && phonesLookDifferent(nextPhone, prevPhone));

  const willBlockWithdrawals = useMemo(() => {
    if (!emailChanged && !phoneChanged) return false;
    const nextEmailVerified = emailChanged ? false : Boolean(emailVerified);
    const nextPhoneVerified = phoneChanged ? false : Boolean(phoneVerified);
    const hasVerifiedEmail = nextEmailVerified && Boolean(nextEmail || prevEmail);
    const hasVerifiedPhone = nextPhoneVerified && Boolean(nextPhone || prevPhone);
    return !hasVerifiedEmail && !hasVerifiedPhone;
  }, [
    emailChanged,
    phoneChanged,
    emailVerified,
    phoneVerified,
    nextEmail,
    nextPhone,
    prevEmail,
    prevPhone,
  ]);

  const showRiskConfirm = willBlockWithdrawals || serverRiskHint != null;
  const riskMessage =
    serverRiskHint ||
    'This change leaves no verified contact. Withdrawals will be blocked until the user re-verifies.';

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const body: ContactBody = { reason: reason.trim() };
    if (emailChanged) body.email = nextEmail;
    if (phoneChanged) body.phone = nextPhone;

    if (!body.email && !body.phone) {
      setError('Change email and/or phone before saving.');
      return;
    }
    if (body.reason.length < 3) {
      setError('Reason is required (min 3 characters).');
      return;
    }
    if (showRiskConfirm && !confirmRisk) {
      setError('Confirm the withdrawal risk checkbox before saving.');
      return;
    }
    if (confirmRisk) body.confirmRisk = true;

    setSubmitting(true);
    try {
      await patchContact(target, body);
      onSuccess?.();
      onClose();
    } catch (err) {
      const parsed = extractError(err);
      if (parsed.code === 'CONFIRM_RISK_REQUIRED') {
        const pending = parsed.preflight?.pendingWithdrawalCount ?? 0;
        setServerRiskHint(
          `This change leaves no verified contact. Withdrawals will be blocked until re-verify` +
            (pending > 0 ? ` (${pending} open withdrawal(s)).` : '.') +
            ' Check the box below and save again.'
        );
        setConfirmRisk(false);
      } else {
        setError(parsed.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-update-contact-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="space-y-2 pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle id="admin-update-contact-title" className="text-lg pr-8">
              {title}
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground font-normal">
            Changed fields reset verification. Admin cannot mark verified. User must re-verify on their portal.
            {target.kind === 'player'
              ? ' Changing player email also disables 2FA (email OTP would lock login until re-verify).'
              : ''}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="admin-contact-email" className="text-sm font-medium flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                Email
                {emailVerified ? (
                  <span className="text-[10px] font-normal text-green-700">verified</span>
                ) : (
                  <span className="text-[10px] font-normal text-muted-foreground">unverified</span>
                )}
              </label>
              <Input
                id="admin-contact-email"
                type="email"
                value={email}
                onChange={(ev) => {
                  setEmail(ev.target.value);
                  setServerRiskHint(null);
                  setConfirmRisk(false);
                }}
                disabled={submitting}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="admin-contact-phone" className="text-sm font-medium flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                Phone
                {phoneVerified ? (
                  <span className="text-[10px] font-normal text-green-700">verified</span>
                ) : (
                  <span className="text-[10px] font-normal text-muted-foreground">unverified</span>
                )}
              </label>
              <PhoneInput
                id="admin-contact-phone"
                value={phone}
                onChange={(v) => {
                  setPhone(v);
                  setServerRiskHint(null);
                  setConfirmRisk(false);
                }}
                disabled={submitting}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="admin-contact-reason" className="text-sm font-medium">
                Reason (required)
              </label>
              <Input
                id="admin-contact-reason"
                value={reason}
                onChange={(ev) => setReason(ev.target.value)}
                disabled={submitting}
                placeholder="Why is this contact being changed?"
              />
            </div>

            {showRiskConfirm && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2">
                <div className="flex gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <p>{riskMessage}</p>
                </div>
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={confirmRisk}
                    onChange={(ev) => setConfirmRisk(ev.target.checked)}
                    disabled={submitting}
                  />
                  I understand withdrawals may be blocked until re-verify
                </label>
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || (showRiskConfirm && !confirmRisk)}>
                {submitting ? 'Saving…' : 'Save contact'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUpdateContactModal;
